import { useEffect, useRef, useState } from "react"
import { previewQrCode, ApiError } from "../lib/api"

const DEBOUNCE_MS = 300

// Renders a live SVG preview of the current design. Debounces the server
// round-trip so dragging a color slider doesn't fire a request per pixel.
// Keeps the last good SVG on screen while a new one is in flight — never
// blanks out, which is jarring during continuous edits.
//
// `design` is the same shape used by createQrCode / updateQrCode: an object
// of camelCase string fields plus an optional File logo.
//
// `maxWidth` caps the preview box width (Tailwind class) — defaults to the
// historic 14rem; the dashboard builder passes 260px so the pre-submit
// preview matches the post-submit ResultCard image and the layout doesn't
// pop in size on create.
// The boxed preview: a constant padding on every side, so a framed code's
// border never sits on the box's border.
const BOX = "p-3 rounded border border-[color:var(--color-border)] dark:border-storm bg-white dark:bg-midnight"
// `pinned` (lib/previewLayout, narrow screens): no box, only the code, at
// most a quarter of the screen high. The SVG keeps its shape and centers in
// that height.
const PINNED = "[&_svg]:max-h-[24svh]"

export default function LivePreview({ design, fallbackSvg, maxWidth = "max-w-[14rem]", pinned = false }) {
  const [svg, setSvg] = useState(fallbackSvg || null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const requestSeq = useRef(0)

  useEffect(() => {
    const seq = ++requestSeq.current
    const timeout = setTimeout(async () => {
      setLoading(true)
      try {
        const body = await previewQrCode(design)
        if (seq !== requestSeq.current) return  // stale response, newer one in flight
        setSvg(body.svg)
        setError(null)
      } catch (e) {
        if (seq !== requestSeq.current) return
        setError(e instanceof ApiError ? e.message : "Couldn't render preview.")
      } finally {
        if (seq === requestSeq.current) setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => clearTimeout(timeout)
  }, [design.token, design.referenceToken, design.foregroundColor, design.backgroundColor, design.gradientColor, design.gradientType, design.frameStyle, design.headerText, design.footerText, design.textWeight, design.moduleShape, design.eyeShape, design.logo, design.logoSize, design.removeLogo, design.wordmark])

  return (
    <div className="relative">
      <div
        // No loading fade — the preview is a real, always-crisp render of the
        // current design; aria-busy still signals in-flight updates to AT.
        // A constant padding on every side, and the box takes the image's own
        // height: a framed code's border never sits on the box's border, and
        // a code with text above or below (taller than wide) is not
        // letterboxed (#305). The image still carries its quiet zone (#262).
        // Square only until the first render, as a placeholder.
        className={`${svg ? "" : (pinned ? "h-[24svh] " : "aspect-square ")}w-full ${maxWidth} mx-auto ${pinned ? PINNED : BOX} overflow-hidden`}
        aria-busy={loading}
        aria-label="QR code preview"
      >
        {svg
          // Safe: `svg` is server-generated markup from Qr::Renderer (the
          // /preview + /restyle endpoints). Header and footer text arrive as
          // outline paths, never as text. Do not pipe any user-supplied string
          // through here — it would become XSS.
          ? <div className="w-full [&_svg]:block [&_svg]:w-full [&_svg]:h-auto" dangerouslySetInnerHTML={{ __html: svg }} />
          : <div className="w-full h-full flex items-center justify-center text-sm text-[color:var(--color-muted)] dark:text-muted-light">Rendering…</div>
        }
      </div>
      {error && (
        <p className="mt-2 text-xs text-[color:var(--color-fail)] text-center">{error}</p>
      )}
    </div>
  )
}
